import React from "react";
import { EntityEditProvider } from "@/contexts/EntityEditContext";
import type { EntityEditProviderProps } from "@/types/contexts/entityEdit";
import { ApplicationType } from "@/types/db/applications";
import { applicationService } from "@/services/applicationService";

interface ApplicationEditProviderProps extends Omit<EntityEditProviderProps<ApplicationType>, "onSave" | "entityName"> {
  children: React.ReactNode;
}

export function ApplicationEditProvider({
  children,
  entityData,
  loading,
  onSaveSuccess,
}: ApplicationEditProviderProps) {
  const handleSave = async (formData: Partial<ApplicationType>): Promise<ApplicationType> => {
    if (!entityData?.id) {
      throw new Error("No application ID available");
    }

    // Extract readonly fields
    const { id, created_at, ...cleanFormData } = formData;

    // Update the application
    const updatedApplication = await applicationService.updateApplication(entityData.id, cleanFormData);

    // Fetch the complete application data to return
    const completeApplication = await applicationService.getApplicationById(entityData.id);
    if (!completeApplication) {
      throw new Error("Failed to fetch updated application data");
    }

    return completeApplication;
  };

  return (
    <EntityEditProvider
      entityData={entityData}
      loading={loading}
      onSave={handleSave}
      onSaveSuccess={onSaveSuccess}
      entityName="Application"
    >
      {children}
    </EntityEditProvider>
  );
}
